import log from "loglevel";

const LOG_CHANNELS = [
  "appsync",
  "bootstrap",
  "call",
  "db",
  "electron",
  "events",
  "feed",
  "query",
  "recording",
  "reset",
  "storage",
  "telemetry",
  "upload",
  "warmup",
] as const;

export type LogChannel = (typeof LOG_CHANNELS)[number];

const LEVEL_NAMES: Record<number, string> = {
  0: "TRACE",
  1: "DEBUG",
  2: "INFO",
  3: "WARN",
  4: "ERROR",
  5: "SILENT",
};

function prefixMethodFactory(
  methodName: log.LogLevelNames,
  level: log.LogLevelNumbers,
  loggerName: string | symbol,
): log.LoggingMethod {
  const raw = log.methodFactory(methodName, level, loggerName);
  return (...args: Parameters<log.LoggingMethod>) => {
    raw(`[${String(loggerName)}]`, ...args);
  };
}

const loggers = new Map<LogChannel, log.Logger>();

export function createLogger(channel: LogChannel): log.Logger {
  const existing = loggers.get(channel);
  if (existing) return existing;

  const logger = log.getLogger(channel);
  logger.methodFactory = prefixMethodFactory;
  logger.rebuild();
  loggers.set(channel, logger);
  return logger;
}

export function getChannelStates(): { channel: LogChannel; level: string }[] {
  return LOG_CHANNELS.map((channel) => {
    const logger = loggers.get(channel) ?? log.getLogger(channel);
    return { channel, level: LEVEL_NAMES[logger.getLevel()] ?? "WARN" };
  });
}

export function setChannelLevel(channel: LogChannel, level: keyof typeof log.levels): void {
  const logger = loggers.get(channel) ?? createLogger(channel);
  logger.setLevel(log.levels[level]);
}

export function resetAllChannels(): void {
  for (const channel of LOG_CHANNELS) {
    const logger = loggers.get(channel) ?? log.getLogger(channel);
    logger.resetLevel();
    logger.rebuild();
  }
}

export function initializeLogChannels(): void {
  for (const channel of LOG_CHANNELS) {
    createLogger(channel);
  }
}

export { LOG_CHANNELS };
